<script setup lang="ts">
import { defineEmits, defineProps, reactive, ref, RenderFunction, watch } from 'vue'
import { useField } from 'vee-validate'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import { i18n } from '../../../../localization'

import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import { postSignatureDocument } from '../../../../utilities/api'
import { Signature } from '../../../../utilities/account/useAccountDataPersonal'
import BaseInputWrapper from '../../../base/BaseInputWrapper.vue'

// Create component
const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileValidateSize, FilePondPluginImagePreview)

const props = defineProps<{
    modelValue: string
    label: string
    name: string
    icon?: RenderFunction
    required?: boolean
    required2?: boolean
    rules?: any
    signature: Signature
}>()

const { errorMessage, handleChange } = useField(props.name, props.rules, { initialValue: props.modelValue })

const emit = defineEmits(['update:modelValue', 'uploaded', 'update:uploading'])

function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

const pond = ref(null)

const myFiles = reactive([])
watch(myFiles, () => updateValue(myFiles))

const uploading = ref(false)
watch(uploading, () => emit('update:uploading', uploading.value))

function handleFilePondInit() {
    // console.log('FilePond has initialized')
    // FilePond instance methods are available on `this.$refs.pond`
}

function flush() {
    myFiles.splice(0)
}

/**
 *
 * @param fieldName
 * @param file
 * @param metadata
 * @param load
 * @param error
 * @param progress
 * @param abort
 * @param transfer
 * @param options
 *
 * docs: https://pqina.nl/filepond/docs/api/server/#process-1
 */
async function process(fieldName, file, metadata, load, error, progress, abort, transfer, options) {
    const controller = new AbortController()
    uploading.value = true
    await postSignatureDocument(props.signature.id, file, {
        // Should call the progress method to update the progress to 100% before calling load
        // Setting computable to false switches the loading indicator to infinite mode
        onUploadProgress: (e) => progress(e.lengthComputable, e.loaded, e.total),
        signal: controller.signal,
    })
        .then(({ data }) => {
            // Should call the load method when done and pass the returned server file id
            // this server file id is then used later on when reverting or restoring a file
            // so your server knows which file to return without exposing that info to the client

            myFiles.push(data)
            emit('uploaded', data)
            uploading.value = false
            load(data)
        })
        .catch(() => {
            error('oh no')
            uploading.value = false
        })

    return {
        abort: () => {
            // This function is entered if the user has tapped the cancel button
            controller.abort()
            // Let FilePond know the request has been cancelled
            abort()
        },
    }
}
</script>

<template>
    <BaseInputWrapper :name="props.name" :label="props.label" :icon="icon" :required="required" :required2="required2">
        <!--      to deactivate instant upload:   :instant-upload="false"-->
        <file-pond
            ref="pond"
            name="test"
            class="-mb-2"
            :class="{ 'error': !!errorMessage }"
            :label-idle="i18n.global.t('verification.upload_label')"
            :label-invalid-field="i18n.global.t('verification.upload_invalid_field')"
            :label-file-waiting-for-size="i18n.global.t('verification.upload_waiting_for_size')"
            :label-file-size-not-available="i18n.global.t('verification.upload_size_not_available')"
            :label-file-loading="i18n.global.t('verification.upload_loading')"
            :label-file-load-error="i18n.global.t('verification.upload_load_error')"
            :label-file-processing="i18n.global.t('verification.upload_processing')"
            :label-file-processing-complete="i18n.global.t('verification.upload_processing_complete')"
            :label-file-processing-aborted="i18n.global.t('verification.upload_processing_aborted')"
            :label-file-processing-error="i18n.global.t('verification.upload_processing_error')"
            :label-file-processing-revert-error="i18n.global.t('verification.upload_processing_revert_error')"
            :label-file-remove-error="i18n.global.t('verification.upload_file_remove_error')"
            :label-tap-to-cancel="i18n.global.t('verification.upload_tap_to_cancel')"
            :label-tap-to-retry="i18n.global.t('verification.upload_tap_to_retry')"
            :label-tap-to-undo="i18n.global.t('verification.upload_tap_to_undo')"
            :label-button-remove-item="i18n.global.t('verification.upload_button_remove_item')"
            :label-button-abort-item-load="i18n.global.t('verification.upload_button_abort_item_load')"
            :label-button-retry-item-load="i18n.global.t('verification.upload_button_retry_item_load')"
            :label-button-abort-item-processing="i18n.global.t('verification.upload_button_abort_item_processing')"
            :label-button-undo-item-processing="i18n.global.t('verification.upload_button_undo_item_processing')"
            :label-button-retry-item-processing="i18n.global.t('verification.upload_button_retry_item_processing')"
            :label-button-process-item="i18n.global.t('verification.upload_button_process_item')"
            accepted-file-types="application/pdf"
            :server="{ process }"
            :credits="false"
            :allow-multiple="false"
            v-bind="$attrs"
            @removefile="flush"
            @init="handleFilePondInit" />
    </BaseInputWrapper>
</template>
